import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Button, Card, Form } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Message from '../components/Message';
import { addToCart, removeFromCart } from '../slices/cartSlice';
import { useGetProductsQuery } from '../slices/productsApiSlice';


// Optional: If using Redux for products
// import { useSelector } from 'react-redux';
// const productList = useSelector((state) => state.productList.products || []);

// Optional: If using RTK Query
 

const CartForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);
  const { data } = useGetProductsQuery({ pageNumber: 1 });
  const productList = data?.products || [];

  // Replace this with your actual product source
  //const productList = useSelector((state) => state.productList.products || []);

  // 🧠 Excel Upload with Product Matching
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || productList.length === 0) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      rows.forEach((row) => {
        const name = row.name?.trim();
        const qty = parseInt(row.qty, 10);

        if (!name || isNaN(qty)) return;

        const matchedProduct = productList.find(
          (p) => p.name.trim().toLowerCase() === name.toLowerCase()
        );

        if (matchedProduct) {
          dispatch(addToCart({
            _id: matchedProduct._id,
            name: matchedProduct.name,
            price: matchedProduct.price,
            qty,
            countInStock: matchedProduct.countInStock,
            images: matchedProduct.images || [],
          }));
        } else {
          console.warn(`Product "${name}" not found in product list`);
        }
      });
    };
    reader.readAsArrayBuffer(file);
  };

  // 📤 Export Cart to Excel
  const exportCartToExcel = () => {
    const data = cartItems.map((item) => ({
      _id: item._id,
      name: item.name,
      price: item.price,
      qty: item.qty,
      total: item.price * item.qty,
      countInStock: item.countInStock,
      image: item.images?.[0] || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'CartItems');
    XLSX.writeFile(workbook, 'CartItems.xlsx');
  };

  // 🧭 Navigation Targets
  const navTargets = {
    shipping: 'Proceed To Checkout',
    salesform: 'Proceed To SalesForm',
    supplysform: 'Proceed To SupplyForm',
    transfersform: 'Proceed To TransferForm',
    stocksform: 'Proceed To Stock',
    openingstocksform: 'Proceed To Op-Stock',
    expenselist: 'Income / Expenses',
    productlist: 'Itemlist',
    cashlist: 'Cash Collected',
    rentlistscreen: 'Rent Data',
    landlordlist: 'Landlord List',
    TenantlistScreen: 'Tenant List',
    paymentlist: 'Payment Data',
    bankuserlist: 'Bank',
    staffhouselist: 'Create Property/Properties',
    customeruserlist: 'Customerlist',
    supplierlist: 'Supplierlist',
    staffhomescreen: 'Market',
  };

  const navHandler = (key) => {
    navigate(`/login?redirect=/${key}`);
  };

  return (
    <Row>
      {/* 🧾 Left Panel */}
      <Col md={8}>
        <h1 className="mb-4">Browse Pages</h1>

        <Form.Group controlId="excelUpload" className="mb-3">
          <Form.Label>Upload Excel File</Form.Label>
          <Form.Control type="file" accept=".xlsx, .xls" onChange={handleExcelUpload} />
        </Form.Group>

        {cartItems.length === 0 ? (
          <Message>
            Go <Link to="/house/HomeScreen">Back</Link>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row className="align-items-center">
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Thumbnail"
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                      />
                    )}
                  </Col>
                  <Col md={3}>
                    <Link to={`/product/${item._id}`}>{item.name}</Link>
                  </Col>
                  <Col md={2}>${item.price}</Col>
                  <Col md={2}>
                    <Button
                      variant="light"
                      onClick={() => dispatch(addToCart({ ...item, qty: item.qty - 1 }))}
                      disabled={item.qty === 1}
                    >
                      -
                    </Button>
                    <span className="mx-2">{item.qty}</span>
                    <Button
                      variant="light"
                      onClick={() => dispatch(addToCart({ ...item, qty: item.qty + 1 }))}
                      disabled={item.qty === item.countInStock}
                    >
                      +
                    </Button>
                  </Col>
                  <Col md={2}>${(item.price * item.qty).toFixed(2)}</Col>
                  <Col md={1}>
                    <Button variant="light" onClick={() => dispatch(removeFromCart(item._id))}>
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* 📦 Right Panel */}
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)}) items
              </h2>
              ${cartItems.reduce((acc, item) => acc + item.qty * item.price, 0).toFixed(2)}
            </ListGroup.Item>

            <ListGroup.Item>
              {Object.entries(navTargets).map(([key, label]) => (
                <Button
                  key={key}
                  className="btn-block mb-2"
                  disabled={
                    ['checkout', 'salebag', 'supplybag', 'transferbag', 'stockbag', 'openingstockbag'].includes(key) &&
                    cartItems.length === 0
                  }
                  onClick={() => navHandler(key)}
                >
                  {label}
                </Button>
              ))}

              {cartItems.length > 0 && (
                <Button className="btn-block mt-2" onClick={exportCartToExcel}>
                  Download Cart to Excel
                </Button>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default CartForm;